// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Grid2, IconButton, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { TextTooltip } from '~/components/common';
// Others
import { formatPhoneNumber, getFullName } from '~/utils/helper/common';
import { ItemTypeJob } from '~/utils/interface/jobs';
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum } from '~/utils/enum/common';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';

// Styles, images, icons
import styles from './JobKanbanItem.module.scss';
import { icons } from '~/assets';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';

type Props = {
  item: ItemTypeJob;
  isDelete?: boolean;
  onRemove?: (item: ItemTypeJob) => void;
};

const cx = classNames.bind(styles);

const JobKanbanItem = (props: Props) => {
  //#region Destructuring Props
  const { item, isDelete = false, onRemove } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base', 'admin/jobs']);
  const navigate = useNavigate();
  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleView = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.detailJob(item?.id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.detailJob(item?.id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.detailJob(item?.id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.detailJob(item?.id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.detailJob(item?.id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.detailJob(item?.id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.detailJob(item?.id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.detailJob(item?.id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.detailJob(item?.id));
        break;
    }
  }; //#endregion Handle Function

  return (
    <Box id='JobKanbanItemComponent' className={cx('container')}>
      <Stack className={cx('viewBox')}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography
            className={cx('title')}
            onClick={(e) => {
              e.stopPropagation();
              handleView();
            }}
          >
            {item?.title}
          </Typography>

          {isDelete && (
            <IconButton
              className={cx('buttonIcon')}
              onClick={() => {
                onRemove?.(item);
              }}
            >
              <img src={icons.commonIconTrashRed} alt={t('common_alt_img')} />
            </IconButton>
          )}
        </Stack>

        <Stack marginBottom={'11px'}>
          <Typography className={cx('value')}>{`#${item.id}`}</Typography>
        </Stack>

        <Grid2 container direction={'row'} spacing={2} alignItems={'center'}>
          <Grid2 size={6}>
            <Stack direction={'row'} spacing={'6px'} alignItems={'center'}>
              <img className={cx('icon')} src={icons.commonIconUser} alt={t('common_alt_img')} />

              <TextTooltip
                className={cx('value')}
                text={getFullName({ firstName: item?.client?.firstName, lastName: item?.client?.lastName })}
              />
            </Stack>
          </Grid2>

          <Grid2 size={6}>
            <Stack direction={'row'} spacing={'6px'} alignItems={'center'}>
              <img className={cx('icon')} src={icons.commonIconPhone} alt={t('common_alt_img')} />

              <Typography className={cx('value')}>
                {formatPhoneNumber(item?.client?.clientContacts?.[0]?.phoneNumber)}
              </Typography>
            </Stack>
          </Grid2>
        </Grid2>

        <Stack direction={'row'} spacing={2} alignItems={'center'} marginTop={4}>
          <img className={cx('icon')} src={icons.commonIconAddressNew} alt={t('common_alt_img')} />

          <TextTooltip className={cx('value')} text={item?.address?.address} />
        </Stack>

        {/* <Stack direction={'row'} spacing={2} alignItems={'center'} marginTop={4}>
          <BaseStatus type='big' label={formatCurrencyUSD({ value: item.total, showDecimal: true })} />

          {item?.status && <BaseStatus type='big' label={item?.status} />}
        </Stack> */}
      </Stack>

      {/* <Stack className={cx('viewBox', 'viewLine')}>
        <Stack direction={'row'} spacing={2} alignItems={'center'}>
          <img className={cx('icon')} src={icons.commonIconTime} alt={t('common_alt_img')} />

          <Typography className={cx('value')}>{`${t('job_kanban_item_title_event_date', {
            ns: 'admin/jobs',
          })} ${formatDate(item.startDate)}`}</Typography>
        </Stack>
      </Stack> */}
    </Box>
  );
};

export default JobKanbanItem;
